/**
 * Generic OpenAI Realtime WebRTC transport (extractable).
 *
 * Opens a peer connection straight to OpenAI using a short-lived ephemeral
 * client_secret: adds the mic track, wires an <audio> sink for the agent voice,
 * opens the `oai-events` data channel, and exchanges SDP with the Realtime
 * "calls" endpoint. It knows nothing about story briefs — the caller parses the
 * JSON events it forwards.
 */

/** OpenAI Realtime WebRTC SDP-exchange endpoint. */

export interface RealtimeConnection {
  pc: RTCPeerConnection;
  dc: RTCDataChannel;
  /** The provider call id (rtc_…) from the /calls response `Location` header —
   *  the server uses it to open the trusted-usage sideband. null if absent. */
  callId: string | null;
  /** Send a JSON client event over the data channel (best-effort). */
  send: (event: unknown) => void;
  /** Tear everything down: data channel, tracks, peer connection. */
  close: () => void;
}

export interface ConnectOptions {
  ephemeralKey: string;
  model: string;
  /** SDP-exchange endpoint (Azure vs OpenAI host). Defaults to OpenAI direct. */
  callsUrl?: string;
  /** Local mic stream (already obtained via getUserMedia). */
  micStream: MediaStream;
  /** Sink element for the agent's remote audio. */
  audioEl: HTMLAudioElement;
  /** Parsed server events from the `oai-events` data channel. */
  onEvent: (event: Record<string, unknown>) => void;
  /** Transport-level failures (SDP exchange, ICE, channel error). */
  onError: (err: Error) => void;
  /** Fired once the data channel is open. */
  onOpen?: () => void;
}

/**
 * Establish the realtime connection. Resolves once the SDP answer is applied
 * (the data channel may open slightly later — use onOpen for that).
 */
export async function connectRealtime(opts: ConnectOptions): Promise<RealtimeConnection> {
  const { ephemeralKey, model, callsUrl, micStream, audioEl, onEvent, onError, onOpen } = opts;

  const pc = new RTCPeerConnection();

  // Agent voice → <audio> sink.
  pc.ontrack = (e) => {
    if (e.streams[0]) audioEl.srcObject = e.streams[0];
  };
  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'failed') onError(new Error('realtime connection failed'));
  };

  // Mic → OpenAI.
  for (const track of micStream.getAudioTracks()) pc.addTrack(track, micStream);

  const dc = pc.createDataChannel('oai-events');
  dc.onopen = () => onOpen?.();
  dc.onerror = () => onError(new Error('realtime data channel error'));
  dc.onmessage = (e) => {
    try {
      onEvent(JSON.parse(e.data));
    } catch {
      /* non-JSON keepalive — ignore */
    }
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // The model is bound to the ephemeral token at mint time (session.model), so
  // it is NOT re-specified on the /calls SDP POST (current Realtime WebRTC API).
  // callsUrl = the `webrtc_url` the worker mint returned (the provider/host
  // follows the model the worker chose) — no host literal lives here.
  void model;
  if (!callsUrl) throw new Error('realtime mint returned no webrtc_url');
  const resp = await fetch(callsUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ephemeralKey}`,
      'Content-Type': 'application/sdp',
    },
    body: offer.sdp ?? '',
  });
  if (!resp.ok) {
    pc.close();
    throw new Error(`realtime SDP exchange failed: ${resp.status}`);
  }
  // The call id (rtc_…) rides in the Location header (/v1/realtime/calls/rtc_xxx);
  // the server needs it to open the trusted-usage sideband.
  const callId = resp.headers.get('Location')?.split('/').pop() ?? null;
  const answerSdp = await resp.text();
  await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

  const send = (event: unknown) => {
    if (dc.readyState === 'open') {
      try {
        dc.send(JSON.stringify(event));
      } catch {
        /* channel closed mid-send — ignore */
      }
    }
  };

  const close = () => {
    try {
      dc.close();
    } catch {
      /* already closed */
    }
    for (const track of micStream.getTracks()) {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    }
    try {
      pc.getSenders().forEach((s) => s.track?.stop());
    } catch {
      /* ignore */
    }
    try {
      pc.close();
    } catch {
      /* already closed */
    }
    audioEl.srcObject = null;
  };

  return { pc, dc, callId, send, close };
}
